// /market-metrics — market + credit-market flow dashboard (staff-only).
// Epic #550 Phase 1, issue #564.
//
// Shows item sales (count + cash volume), listing activity (count + gross),
// estimated listing fees (when config present), and credit-market trades
// (credits ₢ traded + cash ¥ paid).
// D-Currency-1: cash = ¥ (U+00A5), credits = ₢ (U+20A2).

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { EconomyDashboardLayout } from '@/components/admin/economy-dashboard-layout';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ItemSales {
  sale_count: number;
  cash_volume: number;
}

interface ListingFee {
  rate: number;
  estimated_fees: number;
}

interface ItemListings {
  listing_count: number;
  listing_gross: number;
  listing_fee: ListingFee | null;
}

interface CreditTrades {
  trade_count: number;
  credits_traded: number;
  cash_paid: number;
}

interface MetricsResponse {
  ok: boolean;
  range: { since: number | null; until: number | null };
  item_sales: ItemSales;
  item_listings: ItemListings;
  credit_trades: CreditTrades;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Format an integer money value with thousands separators and the correct
 *  currency symbol per D-Currency-1 (cash = ¥ U+00A5, credits = ₢ U+20A2). */
function formatMoney(value: number, currency: string): string {
  const symbol = currency === 'credits' ? '₢' : '¥';
  return `${symbol}${value.toLocaleString()}`;
}

function buildQueryString(since: string, until: string, userId: string): string {
  const params = new URLSearchParams();
  if (since.trim()) params.set('since', since.trim());
  if (until.trim()) params.set('until', until.trim());
  if (userId.trim()) params.set('userId', userId.trim());
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ── Component ─────────────────────────────────────────────────────────────────

export function MarketMetricsPage() {
  // Filter form state (draft; applied on "Apply" click)
  const [draftSince, setDraftSince] = useState('');
  const [draftUntil, setDraftUntil] = useState('');
  const [draftUserId, setDraftUserId] = useState('');

  // Applied filters (trigger query)
  const [appliedSince, setAppliedSince] = useState('');
  const [appliedUntil, setAppliedUntil] = useState('');
  const [appliedUserId, setAppliedUserId] = useState('');

  const { data, isLoading, isError } = useQuery<MetricsResponse>({
    queryKey: ['market-metrics', appliedSince, appliedUntil, appliedUserId],
    queryFn: async () => {
      const qs = buildQueryString(appliedSince, appliedUntil, appliedUserId);
      const res = await apiFetch<MetricsResponse>(`/admin/market-metrics${qs}`);
      if (!res.ok) throw new Error('Failed to load market metrics.');
      return res.data;
    },
  });

  function handleApply() {
    setAppliedSince(draftSince);
    setAppliedUntil(draftUntil);
    setAppliedUserId(draftUserId);
  }

  return (
    <EconomyDashboardLayout
      title="Market Metrics"
      description="Item market sales, listing activity, and credit-market trades. Defaults to all-time; narrow by Unix timestamp range."
      since={draftSince}
      until={draftUntil}
      onSinceChange={setDraftSince}
      onUntilChange={setDraftUntil}
      userId={draftUserId}
      onUserIdChange={setDraftUserId}
      onApply={handleApply}
      isLoading={isLoading}
      isError={isError}
      errorMessage="Failed to load market metrics."
    >
      {data != null && (
        <>
          {/* Item Sales */}
          <section className="space-y-2">
            <h2 className="text-lg font-medium text-ink-high">Item sales</h2>
            <div className="overflow-x-auto rounded-sm border border-border">
              <table className="w-full text-sm" data-testid="market-item-sales-table">
                <thead className="bg-muted text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium text-ink-mid">Sales (count)</th>
                    <th className="px-3 py-2 font-medium text-ink-mid">Cash transferred (¥)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-panel-elev">
                    <td className="px-3 py-2 tabular-nums">{data.item_sales.sale_count.toLocaleString()}</td>
                    <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(data.item_sales.cash_volume, 'cash')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Item Listings */}
          <section className="space-y-2">
            <h2 className="text-lg font-medium text-ink-high">Item listings</h2>
            {data.item_listings.listing_fee == null && (
              <p className="text-sm text-ink-low italic">
                Listing fee not tracked — listing gross only (config key <code>market.listing_fee_rate</code> absent).
              </p>
            )}
            <div className="overflow-x-auto rounded-sm border border-border">
              <table className="w-full text-sm" data-testid="market-item-listings-table">
                <thead className="bg-muted text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium text-ink-mid">Listings (count)</th>
                    <th className="px-3 py-2 font-medium text-ink-mid">Gross listed (¥)</th>
                    {data.item_listings.listing_fee != null && (
                      <>
                        <th className="px-3 py-2 font-medium text-ink-mid">Fee rate</th>
                        <th className="px-3 py-2 font-medium text-ink-mid">Est. fees (¥)</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-panel-elev">
                    <td className="px-3 py-2 tabular-nums">{data.item_listings.listing_count.toLocaleString()}</td>
                    <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(data.item_listings.listing_gross, 'cash')}</td>
                    {data.item_listings.listing_fee != null && (
                      <>
                        <td className="px-3 py-2 tabular-nums text-ink-low">{(data.item_listings.listing_fee.rate * 100).toFixed(2)}%</td>
                        <td className="px-3 py-2 font-mono text-xs tabular-nums text-amber-700">
                          {formatMoney(data.item_listings.listing_fee.estimated_fees, 'cash')} <span className="text-xs font-normal">(est.)</span>
                        </td>
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Credit Market Trades */}
          <section className="space-y-2">
            <h2 className="text-lg font-medium text-ink-high">Credit-market trades</h2>
            <div className="overflow-x-auto rounded-sm border border-border">
              <table className="w-full text-sm" data-testid="market-credit-trades-table">
                <thead className="bg-muted text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium text-ink-mid">Trades (count)</th>
                    <th className="px-3 py-2 font-medium text-ink-mid">Credits traded (₢)</th>
                    <th className="px-3 py-2 font-medium text-ink-mid">Cash paid (¥)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-panel-elev">
                    <td className="px-3 py-2 tabular-nums">{data.credit_trades.trade_count.toLocaleString()}</td>
                    <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(data.credit_trades.credits_traded, 'credits')}</td>
                    <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(data.credit_trades.cash_paid, 'cash')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </EconomyDashboardLayout>
  );
}
